import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaReply,
  FaTimes,
  FaUsers,
  FaArrowLeft,
  FaTrash,
  FaFlag,
  FaThumbtack,
  FaGavel,
  FaBan,
  FaMicrophoneSlash,
  FaMicrophone,
  FaUserCheck,
  FaCheckCircle,
  FaChevronUp,
  FaChevronDown,
  FaPaperclip,
  FaSmile,
  FaImage,
  FaLink,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";
import { FiLoader, FiMessageCircle, FiSend } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { useChat } from "../context/ChatContext";

const ChatPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const {
    messages,
    sendMessage,
    sendTyping,
    isLoading,
    isConnected,
    isMuted,
    isBanned,
    activeUsersCount,
    activeUsers,
    addReaction,
    deleteMessage,
    pinMessage,
    reportMessage,
    loadMessages,
    scrollToBottom,
    messagesEndRef,
  } = useChat();

  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(null);
  const [warningReason, setWarningReason] = useState("");
  const [muteDuration, setMuteDuration] = useState(7);
  const [banDuration, setBanDuration] = useState(30);
  const [actionReason, setActionReason] = useState("");
  const [joinNotifications, setJoinNotifications] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedModal, setShowPinnedModal] = useState(false);
  const [isPinnedBarExpanded, setIsPinnedBarExpanded] = useState(true);
  const [globalPinnedMessages, setGlobalPinnedMessages] = useState([]);
  const [isPinning, setIsPinning] = useState(false);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const pinnedBarRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // 6 Reaction Emojis (Discord-like)
  const reactions = ["👍", "❤️", "😂", "😮", "🎉", "🔥"];

  const isAdminOrMod = user?.role === "admin" || user?.role === "moderator";

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Load user's pinned messages from localStorage
  useEffect(() => {
    const savedPins = localStorage.getItem(
      `pinned_messages_${user?.id || user?._id}`,
    );
    if (savedPins) {
      setPinnedMessages(JSON.parse(savedPins));
    }
  }, [user]);

  // Save pinned messages to localStorage
  const savePinnedMessages = (pins) => {
    localStorage.setItem(
      `pinned_messages_${user?.id || user?._id}`,
      JSON.stringify(pins),
    );
    setPinnedMessages(pins);
  };

  // Extract globally pinned messages from regular messages
  useEffect(() => {
    const globalPins = messages.filter((msg) => msg.isPinned === true);
    setGlobalPinnedMessages(globalPins);
  }, [messages]);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate("/login");
    }
  }, [isAuthenticated, user, navigate]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768 && showSidebar) {
        setShowSidebar(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showSidebar]);

  // Load messages
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Fetch pending reports for admin
  useEffect(() => {
    if (showAdminPanel && isAdminOrMod) {
      fetchPendingReports();
    }
  }, [showAdminPanel, isAdminOrMod]);

  // Focus input
  useEffect(() => {
    if (inputRef.current && !isMuted && !isBanned) {
      inputRef.current.focus();
    }
  }, [isMuted, isBanned]);

  // Scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Listen for join/leave events
  useEffect(() => {
    const socket = window.chatSocket;
    if (socket) {
      const handleUserJoined = (data) => {
        const id = Date.now();
        setJoinNotifications((prev) => [
          ...prev,
          { id, message: `${data.name} joined the chat`, type: "join" },
        ]);
        setTimeout(() => {
          setJoinNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 3000);
      };

      const handleUserLeft = (data) => {
        const id = Date.now();
        setJoinNotifications((prev) => [
          ...prev,
          { id, message: `${data.name} left the chat`, type: "leave" },
        ]);
        setTimeout(() => {
          setJoinNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 3000);
      };

      socket.on("user_joined", handleUserJoined);
      socket.on("user_left", handleUserLeft);

      return () => {
        socket.off("user_joined", handleUserJoined);
        socket.off("user_left", handleUserLeft);
      };
    }
  }, []);

  const fetchPendingReports = async () => {
    setLoadingReports(true);
    try {
      const response = await axios.get("/api/chat/reports/pending");
      setReports(response.data.reports);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleReviewReport = async (
    reportId,
    status,
    actionTaken,
    adminNote,
  ) => {
    try {
      await axios.put(`/api/chat/report/${reportId}/review`, {
        status,
        actionTaken,
        adminNote,
      });
      toast.success(`Report ${status}`);
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to review report");
    }
  };

  const handleIssueWarning = async (userId, reason) => {
    try {
      const response = await axios.post(`/api/chat/user/${userId}/warning`, {
        reason,
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.isMuted) {
          toast.error("User has been automatically muted due to 3 warnings");
        }
        fetchPendingReports();
      }
      setShowWarningModal(null);
      setWarningReason("");
    } catch (error) {
      console.error("Warning error:", error);
      toast.error(error.response?.data?.message || "Failed to issue warning");
    }
  };

  const handleMuteUser = async (userId) => {
    try {
      await axios.post(`/api/chat/user/${userId}/mute`, {
        duration: muteDuration,
        reason: actionReason,
      });
      toast.success(`User muted for ${muteDuration} days`);
      setSelectedUser(null);
      setActionReason("");
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to mute user");
    }
  };

  const handleUnmuteUser = async (userId) => {
    try {
      await axios.post(`/api/chat/user/${userId}/unmute`);
      toast.success("User unmuted successfully");
      setSelectedUser(null);
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to unmute user");
    }
  };

  const handleBanUser = async (userId) => {
    try {
      await axios.post(`/api/chat/user/${userId}/ban`, {
        duration: banDuration,
        reason: actionReason,
      });
      toast.success(`User banned for ${banDuration} days`);
      setSelectedUser(null);
      setActionReason("");
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to ban user");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`/api/chat/user/${userId}/unban`);
      toast.success("User unbanned successfully");
      setSelectedUser(null);
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to unban user");
    }
  };

  const handleTyping = useCallback(
    (value) => {
      setInputMessage(value);

      if (!isTyping && value.length > 0) {
        setIsTyping(true);
        sendTyping(true);
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        if (isTyping) {
          setIsTyping(false);
          sendTyping(false);
        }
      }, 1500);
    },
    [isTyping, sendTyping],
  );

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (isMuted) {
      toast.error("You are muted. Cannot send messages.");
      return;
    }

    if (isBanned) {
      toast.error("You are banned from chat.");
      return;
    }

    if (!inputMessage.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    if (inputMessage.length > 2000) {
      toast.error("Message too long (max 2000 characters)");
      return;
    }

    sendMessage(inputMessage, replyTo?._id);
    setInputMessage("");
    setReplyTo(null);
    sendTyping(false);
    inputRef.current?.focus();
  };

  const handleReply = (message) => {
    setReplyTo(message);
    inputRef.current?.focus();
    setHoveredMessageId(null);
    setShowReactionPicker(null);
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const handleReaction = (messageId, emoji) => {
    addReaction(messageId, emoji);
    setShowReactionPicker(null);
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      await deleteMessage(messageId);
      toast.success("Message deleted");
    }
    setHoveredMessageId(null);
  };

  // Fixed Pin Message Handler - with single toast notification
  const handlePinMessage = async (message) => {
    if (isPinning) return;
    setIsPinning(true);

    const isAlreadyPinned = pinnedMessages.some((m) => m._id === message._id);

    if (isAdminOrMod) {
      try {
        await pinMessage(message._id);
        toast.success(message.isPinned ? "Message unpinned" : "Message pinned");
        await loadMessages();
      } catch (error) {
        toast.error("Failed to pin/unpin message");
      }
    } else {
      if (isAlreadyPinned) {
        const updatedPins = pinnedMessages.filter((m) => m._id !== message._id);
        savePinnedMessages(updatedPins);
        toast.success("Removed from your pins");
      } else {
        const newPins = [
          ...pinnedMessages,
          {
            _id: message._id,
            message: message.message,
            senderName: message.senderName,
            senderRole: message.senderRole,
            createdAt: message.createdAt,
            pinnedByMe: true,
          },
        ];
        savePinnedMessages(newPins);
        toast.success("Message pinned to your chat");
      }
    }
    setHoveredMessageId(null);
    setTimeout(() => setIsPinning(false), 500);
  };

  // Scroll to a specific message
  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`msg-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      messageElement.style.transition = "background 0.3s";
      messageElement.style.background = "rgba(139,92,246,0.2)";
      setTimeout(() => {
        messageElement.style.background = "";
      }, 2000);
    } else {
      toast.error("Message not found in current view");
    }
  };

  // Unpin from global (admin only)
  const handleUnpinGlobal = async (messageId) => {
    if (!isAdminOrMod) return;
    try {
      await pinMessage(messageId);
      toast.success("Message unpinned");
      await loadMessages();
    } catch (error) {
      toast.error("Failed to unpin message");
    }
  };

  const isMessagePinned = (messageId) => {
    return pinnedMessages.some((m) => m._id === messageId);
  };

  const isGloballyPinned = (message) => {
    return message.isPinned === true;
  };

  const handleReportSubmit = async (messageId) => {
    if (!reportReason) {
      toast.error("Please select a reason");
      return;
    }
    const success = await reportMessage(
      messageId,
      reportReason,
      reportDescription,
    );
    if (success) {
      setShowReportModal(null);
      setReportReason("");
      setReportDescription("");
    }
    setHoveredMessageId(null);
  };

  const getMessageKey = (message, index) => {
    return `${message._id}-${message.createdAt}-${index}`;
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  // Combine global and user pinned messages for display
  const allPinnedMessages = [
    ...globalPinnedMessages.map((msg) => ({ ...msg, isGlobal: true })),
    ...pinnedMessages
      .filter((pm) => !globalPinnedMessages.some((gm) => gm._id === pm._id))
      .map((pm) => ({ ...pm, isGlobal: false })),
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Join/Leave Notifications */}
      <div
        style={{
          position: "fixed",
          top: "80px",
          right: "20px",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <AnimatePresence>
          {joinNotifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              style={{
                background:
                  notification.type === "join"
                    ? "linear-gradient(135deg, #10b981, #059669)"
                    : "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "white",
                padding: "10px 16px",
                borderRadius: "12px",
                fontSize: "13px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              }}
            >
              {notification.type === "join" ? "👋" : "👋"}
              {notification.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Header */}
      <div
        style={{
          padding: isMobile ? "12px 16px" : "16px 24px",
          background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "12px" : "16px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "10px",
              padding: isMobile ? "6px 10px" : "8px 12px",
              cursor: "pointer",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: isMobile ? "12px" : "14px",
              transition: "all 0.3s",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
            }
          >
            <FaArrowLeft size={isMobile ? 12 : 14} /> Back
          </button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HiOutlineSparkles size={18} color="white" />
              </div>
              <h1
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "white", // Make sure text is white
                }}
              >
                <FiMessageCircle size={isMobile ? 18 : 22} /> Community Chat
              </h1>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "4px",
                marginLeft: "44px",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  background: isConnected ? "#10b981" : "#ef4444",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: isConnected ? "pulse 1.5s infinite" : "none",
                }}
              />
              <span
                style={{
                  fontSize: isMobile ? "10px" : "12px",
                  opacity: 0.9,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                {isConnected ? `${activeUsersCount} online` : "Connecting..."}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowSidebar(!showSidebar)}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: "20px",
            padding: isMobile ? "6px 12px" : "8px 16px",
            cursor: "pointer",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: isMobile ? "11px" : "13px",
            transition: "all 0.3s",
            backdropFilter: "blur(10px)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
          }
        >
          <FaUsers size={isMobile ? 12 : 14} />
          {!isMobile &&
            (showSidebar ? "Hide Users" : `Users (${activeUsersCount})`)}
          {isMobile && `Users (${activeUsersCount})`}
        </button>
      </div>

      {/* PROFESSIONAL STICKY PINNED MESSAGES BAR - Discord/Slack Style */}
      {allPinnedMessages.length > 0 && (
        <div
          ref={pinnedBarRef}
          style={{
            background: isDarkMode ? "#1a1a2e" : "#f5f7fb",
            borderBottom: `1px solid ${getBorderColor()}`,
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 98,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 16px",
              cursor: "pointer",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderBottom: `1px solid ${getBorderColor()}`,
            }}
            onClick={() => setIsPinnedBarExpanded(!isPinnedBarExpanded)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.2)"
                    : "rgba(139,92,246,0.1)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaThumbtack size={12} color="#8b5cf6" />
              </div>
              <div>
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "13px",
                    color: getTextColor(),
                  }}
                >
                  Pinned Messages
                </span>
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "11px",
                    color: getTextSecondary(),
                    background: isDarkMode
                      ? "rgba(139,92,246,0.2)"
                      : "rgba(139,92,246,0.1)",
                    padding: "2px 6px",
                    borderRadius: "12px",
                  }}
                >
                  {allPinnedMessages.length}
                </span>
              </div>
            </div>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: getTextSecondary(),
                padding: "4px",
                display: "flex",
                alignItems: "center",
                borderRadius: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode
                  ? "rgba(139,92,246,0.2)"
                  : "rgba(139,92,246,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              {isPinnedBarExpanded ? (
                <FaChevronUp size={12} />
              ) : (
                <FaChevronDown size={12} />
              )}
            </button>
          </div>

          {isPinnedBarExpanded && (
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
              }}
            >
              {allPinnedMessages.map((pinnedMsg, idx) => (
                <div
                  key={idx}
                  onClick={() => scrollToMessage(pinnedMsg._id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 16px",
                    cursor: "pointer",
                    transition: "background 0.2s",
                    borderBottom: `1px solid ${getBorderColor()}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: pinnedMsg.isGlobal ? "#f59e0b" : "#8b5cf6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    {pinnedMsg.senderName?.charAt(0).toUpperCase()}
                  </div>

                  {/* Message Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: getTextColor(),
                        }}
                      >
                        {pinnedMsg.senderName}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: getTextSecondary(),
                        }}
                      >
                        {formatTime(pinnedMsg.createdAt)}
                      </span>
                      {pinnedMsg.isGlobal && (
                        <span
                          style={{
                            fontSize: "9px",
                            background: "#f59e0b",
                            color: "#333",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: "500",
                          }}
                        >
                          Admin Pinned
                        </span>
                      )}
                      {!pinnedMsg.isGlobal && (
                        <span
                          style={{
                            fontSize: "9px",
                            background: isDarkMode
                              ? "rgba(139,92,246,0.2)"
                              : "rgba(139,92,246,0.1)",
                            color: getTextSecondary(),
                            padding: "2px 6px",
                            borderRadius: "4px",
                          }}
                        >
                          Your Pin
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        margin: "4px 0 0 0",
                        color: getTextSecondary(),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pinnedMsg.message}
                    </p>
                  </div>

                  {/* Unpin Button */}
                  {pinnedMsg.isGlobal && isAdminOrMod && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnpinGlobal(pinnedMsg._id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: getTextSecondary(),
                        padding: "6px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDarkMode
                          ? "rgba(139,92,246,0.2)"
                          : "rgba(139,92,246,0.1)";
                        e.currentTarget.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = getTextSecondary();
                      }}
                      title="Unpin"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                  {!pinnedMsg.isGlobal && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedPins = pinnedMessages.filter(
                          (p) => p._id !== pinnedMsg._id,
                        );
                        savePinnedMessages(updatedPins);
                        toast.success("Removed from pins");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: getTextSecondary(),
                        padding: "6px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDarkMode
                          ? "rgba(139,92,246,0.2)"
                          : "rgba(139,92,246,0.1)";
                        e.currentTarget.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = getTextSecondary();
                      }}
                      title="Unpin"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Floating Pinned Indicator (when bar is collapsed) */}
      {allPinnedMessages.length > 0 && !isPinnedBarExpanded && (
        <button
          onClick={() => setIsPinnedBarExpanded(true)}
          style={{
            position: "fixed",
            top: "70px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 99,
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "30px",
            padding: "6px 16px",
            color: getTextColor(),
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "500",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDarkMode
              ? "rgba(139,92,246,0.2)"
              : "rgba(139,92,246,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = getCardBackground();
          }}
        >
          <FaThumbtack size={12} color="#8b5cf6" />
          {allPinnedMessages.length} pinned message
          {allPinnedMessages.length !== 1 ? "s" : ""}
        </button>
      )}

      {/* Main Content */}
      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: showSidebar && !isMobile ? "calc(100% - 320px)" : "100%",
            transition: "width 0.3s ease",
          }}
        >
          {/* Messages Container */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "12px" : "20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FiLoader size={40} color="#8b5cf6" />
                  </motion.div>
                  <p
                    style={{
                      marginTop: "12px",
                      color: getTextSecondary(),
                    }}
                  >
                    Loading messages...
                  </p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: getTextSecondary(),
                }}
              >
                <FiMessageCircle
                  size={48}
                  style={{ marginBottom: "16px", opacity: 0.5 }}
                />
                <p>No messages yet. Be the first to say hello! 👋</p>
              </div>
            ) : (
              messages.map((message, index) => {
                const isCurrentUser =
                  message.sender?._id === user?.id ||
                  message.sender?._id === user?._id;
                const isUserPinned = isMessagePinned(message._id);
                const isGlobalPinned = isGloballyPinned(message);

                return (
                  <motion.div
                    key={getMessageKey(message, index)}
                    id={`msg-${message._id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      display: "flex",
                      justifyContent: isCurrentUser ? "flex-end" : "flex-start",
                      position: "relative",
                    }}
                    onMouseEnter={() => setHoveredMessageId(message._id)}
                    onMouseLeave={() => {
                      setHoveredMessageId(null);
                      setShowReactionPicker(null);
                    }}
                  >
                    <div
                      style={{
                        maxWidth: isMobile ? "85%" : "70%",
                        background: isCurrentUser
                          ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                          : getCardBackground(),
                        borderRadius: "20px",
                        padding: isMobile ? "10px 14px" : "12px 16px",
                        position: "relative",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border: isGlobalPinned ? "2px solid #f59e0b" : "none",
                        backdropFilter: isCurrentUser ? "none" : "blur(20px)",
                      }}
                    >
                      {/* Pinned Badge */}
                      {(isGlobalPinned || isUserPinned) && (
                        <div
                          style={{
                            position: "absolute",
                            top: "-10px",
                            right: "10px",
                            background: isGlobalPinned ? "#f59e0b" : "#8b5cf6",
                            color: "white",
                            fontSize: "9px",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <FaThumbtack size={8} />
                          {isGlobalPinned ? "Pinned" : "Your Pin"}
                        </div>
                      )}

                      {!isCurrentUser && (
                        <div
                          style={{
                            fontSize: isMobile ? "11px" : "12px",
                            fontWeight: "600",
                            color: "#8b5cf6",
                            marginBottom: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          {message.senderName}
                          {message.senderRole === "admin" && (
                            <span
                              style={{
                                fontSize: "9px",
                                background:
                                  "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              Admin
                            </span>
                          )}
                          {message.senderRole === "moderator" && (
                            <span
                              style={{
                                fontSize: "9px",
                                background: "#06b6d4",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              Mod
                            </span>
                          )}
                        </div>
                      )}

                      {message.replyTo && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: isCurrentUser
                              ? "rgba(255,255,255,0.7)"
                              : getTextSecondary(),
                            marginBottom: "4px",
                            paddingLeft: "8px",
                            borderLeft: `2px solid ${isCurrentUser ? "white" : "#8b5cf6"}`,
                          }}
                        >
                          <FaReply size={8} style={{ marginRight: "4px" }} />
                          Replying to {message.replyTo.senderName}
                        </div>
                      )}

                      <p
                        style={{
                          color: isCurrentUser ? "white" : getTextColor(),
                          fontSize: isMobile ? "13px" : "14px",
                          margin: 0,
                          wordWrap: "break-word",
                          lineHeight: "1.5",
                        }}
                      >
                        {message.profanityFiltered ? (
                          <span style={{ fontStyle: "italic" }}>
                            {message.message}
                          </span>
                        ) : (
                          message.message
                        )}
                      </p>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "6px",
                          fontSize: isMobile ? "9px" : "10px",
                          color: isCurrentUser
                            ? "rgba(255,255,255,0.6)"
                            : getTextSecondary(),
                        }}
                      >
                        <span>{formatTime(message.createdAt)}</span>
                        {message.isEdited && <span>(edited)</span>}
                      </div>

                      {/* Reactions Display */}
                      {message.reactions && message.reactions.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginTop: "8px",
                          }}
                        >
                          {Object.entries(
                            message.reactions.reduce((acc, r) => {
                              acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                              return acc;
                            }, {}),
                          ).map(([emoji, count]) => (
                            <button
                              key={emoji}
                              onClick={() => handleReaction(message._id, emoji)}
                              style={{
                                background: isDarkMode
                                  ? "rgba(139,92,246,0.1)"
                                  : "rgba(139,92,246,0.05)",
                                border: "none",
                                padding: "4px 8px",
                                borderRadius: "16px",
                                fontSize: "13px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                transition: "all 0.2s",
                                color: getTextColor(),
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = isDarkMode
                                  ? "rgba(139,92,246,0.2)"
                                  : "rgba(139,92,246,0.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = isDarkMode
                                  ? "rgba(139,92,246,0.1)"
                                  : "rgba(139,92,246,0.05)")
                              }
                            >
                              {emoji}{" "}
                              <span style={{ marginLeft: "2px" }}>{count}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Floating Action Buttons */}
                      {hoveredMessageId === message._id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "-45px",
                            right: isCurrentUser ? "0" : "auto",
                            left: isCurrentUser ? "auto" : "0",
                            background: getCardBackground(),
                            backdropFilter: "blur(20px)",
                            borderRadius: "30px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            padding: "6px 12px",
                            display: "flex",
                            gap: "8px",
                            border: `1px solid ${getBorderColor()}`,
                            zIndex: 100,
                          }}
                        >
                          {/* Reaction Button */}
                          <div style={{ position: "relative" }}>
                            <button
                              onClick={() =>
                                setShowReactionPicker(
                                  showReactionPicker === message._id
                                    ? null
                                    : message._id,
                                )
                              }
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "18px",
                                padding: "6px",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                e.currentTarget.style.background =
                                  "rgba(139,92,246,0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                            >
                              😊
                            </button>

                            {showReactionPicker === message._id && (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "100%",
                                  left: "0",
                                  background: getCardBackground(),
                                  backdropFilter: "blur(20px)",
                                  borderRadius: "30px",
                                  padding: "6px 12px",
                                  display: "flex",
                                  gap: "8px",
                                  border: `1px solid ${getBorderColor()}`,
                                  boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                                  marginBottom: "8px",
                                  zIndex: 101,
                                }}
                              >
                                {reactions.map((emoji) => (
                                  <button
                                    key={emoji}
                                    onClick={() =>
                                      handleReaction(message._id, emoji)
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      fontSize: "20px",
                                      padding: "6px",
                                      borderRadius: "50%",
                                      width: "36px",
                                      height: "36px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1.2)";
                                      e.currentTarget.style.background =
                                        "rgba(139,92,246,0.1)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                      e.currentTarget.style.background =
                                        "transparent";
                                    }}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              width: "1px",
                              background: getBorderColor(),
                            }}
                          />

                          {/* Reply Button */}
                          <button
                            onClick={() => handleReply(message)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "6px",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              color: getTextSecondary(),
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#8b5cf6";
                              e.currentTarget.style.background =
                                "rgba(139,92,246,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = getTextSecondary();
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <FaReply size={14} />
                          </button>

                          {/* Report Button */}
                          {!isCurrentUser && (
                            <button
                              onClick={() => setShowReportModal(message._id)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "6px",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                color: "#ef4444",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(239,68,68,0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                              title="Report this message"
                            >
                              <FaFlag size={14} />
                            </button>
                          )}

                          {/* Delete Button */}
                          {(isCurrentUser || isAdminOrMod) && (
                            <button
                              onClick={() => handleDeleteMessage(message._id)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "6px",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                color: "#ef4444",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "rgba(239,68,68,0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                              title="Delete this message"
                            >
                              <FaTrash size={14} />
                            </button>
                          )}

                          {/* Pin Button */}
                          <button
                            onClick={() => handlePinMessage(message)}
                            disabled={isPinning}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: isPinning ? "wait" : "pointer",
                              padding: "6px",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              color: isMessagePinned(message._id)
                                ? "#8b5cf6"
                                : getTextSecondary(),
                              transition: "all 0.2s",
                              opacity: isPinning ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isPinning) {
                                e.currentTarget.style.background =
                                  "rgba(139,92,246,0.1)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isPinning) {
                                e.currentTarget.style.background =
                                  "transparent";
                              }
                            }}
                            title={
                              isMessagePinned(message._id)
                                ? "Unpin this message"
                                : "Pin this message"
                            }
                          >
                            <FaThumbtack size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Indicator */}
          <AnimatePresence>
            {replyTo && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                style={{
                  padding: isMobile ? "8px 12px" : "10px 16px",
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderTop: `1px solid ${getBorderColor()}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <FaReply size={12} color="#8b5cf6" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#8b5cf6",
                        fontWeight: "500",
                      }}
                    >
                      Replying to {replyTo.senderName}
                    </span>
                    <p
                      style={{
                        fontSize: "10px",
                        color: getTextSecondary(),
                        marginTop: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {replyTo.message.substring(0, 50)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={cancelReply}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: getTextSecondary(),
                    padding: "4px",
                    flexShrink: 0,
                  }}
                >
                  <FaTimes size={12} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            style={{
              padding: isMobile ? "12px 16px" : "16px 24px",
              borderTop: `1px solid ${getBorderColor()}`,
              display: "flex",
              gap: "12px",
              alignItems: "center",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              flexShrink: 0,
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => handleTyping(e.target.value)}
              placeholder={
                isMuted
                  ? "You are muted"
                  : isBanned
                    ? "You are banned"
                    : "Type your message..."
              }
              disabled={isMuted || isBanned}
              style={{
                flex: 1,
                padding: isMobile ? "12px 16px" : "14px 20px",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "28px",
                outline: "none",
                fontSize: isMobile ? "14px" : "15px",
                background: getInputBackground(),
                color: getTextColor(),
                transition: "all 0.3s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
            />

            <button
              type="submit"
              disabled={!inputMessage.trim() || isMuted || isBanned}
              style={{
                background:
                  !inputMessage.trim() || isMuted || isBanned
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                border: "none",
                borderRadius: "50%",
                width: isMobile ? "44px" : "48px",
                height: isMobile ? "44px" : "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor:
                  !inputMessage.trim() || isMuted || isBanned
                    ? "not-allowed"
                    : "pointer",
                transition: "all 0.3s",
                flexShrink: 0,
                boxShadow:
                  !inputMessage.trim() || isMuted || isBanned
                    ? "none"
                    : "0 4px 14px 0 rgba(139,92,246,0.4)",
              }}
            >
              <FiSend size={isMobile ? 18 : 20} color="white" />
            </button>
          </form>

          {/* Connection Status */}
          {!isConnected && (
            <div
              style={{
                padding: "8px",
                textAlign: "center",
                background: "#ef4444",
                color: "white",
                fontSize: "12px",
                flexShrink: 0,
              }}
            >
              Reconnecting to chat server...
            </div>
          )}
        </div>

        {/* Sidebar - Active Users & Admin Panel */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: isMobile ? "100%" : "320px", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                borderLeft: `1px solid ${getBorderColor()}`,
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                position: isMobile ? "fixed" : "relative",
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 100,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowY: "auto",
                boxShadow: isMobile ? "-2px 0 10px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {/* Admin Panel Toggle */}
              {isAdminOrMod && (
                <button
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                  style={{
                    margin: "12px 16px",
                    padding: "12px",
                    background: showAdminPanel
                      ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                      : getCardBackground(),
                    color: showAdminPanel ? "white" : getTextColor(),
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "13px",
                    fontWeight: "500",
                    transition: "all 0.3s",
                  }}
                >
                  <FaGavel size={14} />
                  {showAdminPanel ? "Hide Admin Panel" : "Show Admin Panel"}
                </button>
              )}

              {/* Admin Panel Content */}
              {showAdminPanel && isAdminOrMod && (
                <div
                  style={{
                    margin: "0 16px 16px",
                    padding: "12px",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "rgba(139,92,246,0.03)",
                    borderRadius: "12px",
                    border: `1px solid ${getBorderColor()}`,
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "13px",
                      marginBottom: "12px",
                      color: "#8b5cf6",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FaFlag size={12} /> Pending Reports ({reports.length})
                  </h4>
                  {loadingReports ? (
                    <p style={{ fontSize: "12px", textAlign: "center" }}>
                      Loading...
                    </p>
                  ) : reports.length === 0 ? (
                    <p
                      style={{
                        fontSize: "12px",
                        textAlign: "center",
                        color: getTextSecondary(),
                      }}
                    >
                      No pending reports
                    </p>
                  ) : (
                    reports.map((report) => (
                      <div
                        key={report._id}
                        style={{
                          marginBottom: "12px",
                          padding: "12px",
                          background: getCardBackground(),
                          borderRadius: "10px",
                          fontSize: "12px",
                          border: `1px solid ${getBorderColor()}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          <span>
                            <strong>{report.reportedUser?.name}</strong>{" "}
                            reported by <strong>{report.reporter?.name}</strong>
                          </span>
                          <span
                            style={{
                              color: "#8b5cf6",
                              fontSize: "11px",
                              background: "rgba(139,92,246,0.1)",
                              padding: "2px 8px",
                              borderRadius: "12px",
                            }}
                          >
                            {report.reason}
                          </span>
                        </div>

                        <div
                          style={{
                            background: isDarkMode
                              ? "rgba(139,92,246,0.05)"
                              : "rgba(139,92,246,0.03)",
                            padding: "8px",
                            borderRadius: "8px",
                            marginBottom: "8px",
                            fontSize: "11px",
                            wordBreak: "break-word",
                          }}
                        >
                          <strong>Message:</strong> {report.message}
                        </div>

                        {report.description && (
                          <div
                            style={{
                              background: "rgba(139,92,246,0.1)",
                              padding: "8px",
                              borderRadius: "8px",
                              marginBottom: "10px",
                              fontSize: "11px",
                              borderLeft: "3px solid #8b5cf6",
                            }}
                          >
                            <strong>Additional Details:</strong>{" "}
                            {report.description}
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            onClick={() =>
                              handleReviewReport(
                                report._id,
                                "dismissed",
                                "none",
                                "",
                              )
                            }
                            style={{
                              padding: "5px 10px",
                              background: "#6c757d",
                              border: "none",
                              borderRadius: "6px",
                              color: "white",
                              fontSize: "10px",
                              cursor: "pointer",
                            }}
                          >
                            Dismiss
                          </button>
                          <button
                            onClick={() => {
                              setShowWarningModal(report.reportedUser);
                              setWarningReason("");
                            }}
                            style={{
                              padding: "5px 10px",
                              background: "#f59e0b",
                              border: "none",
                              borderRadius: "6px",
                              color: "#333",
                              fontSize: "10px",
                              cursor: "pointer",
                            }}
                          >
                            ⚠️ Warning (
                            {report.reportedUser?.chatWarningCount || 0}/3)
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(report.reportedUser);
                              setShowAdminPanel(false);
                            }}
                            style={{
                              padding: "5px 10px",
                              background: "#06b6d4",
                              border: "none",
                              borderRadius: "6px",
                              color: "white",
                              fontSize: "10px",
                              cursor: "pointer",
                            }}
                          >
                            Mute/Ban
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Active Users Header */}
              <div
                style={{
                  padding: isMobile ? "12px 16px" : "16px",
                  borderBottom: `1px solid ${getBorderColor()}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <h3
                  style={{
                    fontSize: isMobile ? "14px" : "16px",
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <FaUsers color="#8b5cf6" /> Active Users ({activeUsersCount})
                </h3>
                <button
                  onClick={() => setShowSidebar(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: isMobile ? "16px" : "18px",
                    padding: "4px",
                    color: getTextSecondary(),
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Active Users List */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: isMobile ? "12px" : "16px",
                }}
              >
                {activeUsers.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: getTextSecondary(),
                    }}
                  >
                    No active users
                  </div>
                ) : (
                  activeUsers.map((activeUser) => (
                    <div
                      key={activeUser.userId || activeUser._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px",
                        marginBottom: "8px",
                        background: isDarkMode
                          ? "rgba(139,92,246,0.05)"
                          : "rgba(139,92,246,0.03)",
                        borderRadius: "12px",
                        cursor: isAdminOrMod ? "pointer" : "default",
                        transition: "all 0.2s",
                        border: `1px solid ${getBorderColor()}`,
                      }}
                      onClick={() => {
                        if (
                          isAdminOrMod &&
                          activeUser.userId !== user?.id &&
                          activeUser.userId !== user?._id
                        ) {
                          setSelectedUser(activeUser);
                          setShowAdminPanel(false);
                        }
                      }}
                    >
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <img
                          src={
                            activeUser.profilePicture ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(activeUser.name)}&background=8b5cf6&color=fff&length=1`
                          }
                          alt={activeUser.name}
                          style={{
                            width: isMobile ? "40px" : "44px",
                            height: isMobile ? "40px" : "44px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            bottom: "2px",
                            right: "2px",
                            width: "12px",
                            height: "12px",
                            background: "#10b981",
                            borderRadius: "50%",
                            border: `2px solid ${getCardBackground()}`,
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: isMobile ? "13px" : "14px",
                            wordBreak: "break-word",
                            color: getTextColor(),
                          }}
                        >
                          {activeUser.name}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: getTextSecondary(),
                            marginTop: "2px",
                          }}
                        >
                          {activeUser.role === "admin"
                            ? "Administrator"
                            : activeUser.role === "moderator"
                              ? "Moderator"
                              : "Student"}
                        </div>
                      </div>
                      {isAdminOrMod &&
                        activeUser.userId !== user?.id &&
                        activeUser.userId !== user?._id && (
                          <FaGavel
                            size={12}
                            color="#8b5cf6"
                            style={{ opacity: 0.6 }}
                          />
                        )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Warning Modal */}
      {showWarningModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowWarningModal(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "16px", color: "#f59e0b" }}>
              ⚠️ Issue Warning
            </h3>
            <p
              style={{
                marginBottom: "8px",
                fontSize: "14px",
                color: getTextColor(),
              }}
            >
              User: <strong>{showWarningModal.name}</strong>
            </p>
            <p
              style={{
                marginBottom: "16px",
                fontSize: "12px",
                color: getTextSecondary(),
              }}
            >
              Current warnings:{" "}
              <strong>{showWarningModal.chatWarningCount || 0}/3</strong>
              {(showWarningModal.chatWarningCount || 0) >= 2 && (
                <span style={{ color: "#f59e0b", marginLeft: "8px" }}>
                  (Next warning will auto-mute)
                </span>
              )}
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px",
                  color: getTextColor(),
                }}
              >
                Reason (optional)
              </label>
              <textarea
                value={warningReason}
                onChange={(e) => setWarningReason(e.target.value)}
                rows="2"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "12px",
                  border: `1px solid ${getBorderColor()}`,
                  background: getInputBackground(),
                  resize: "vertical",
                  fontSize: "13px",
                  color: getTextColor(),
                }}
                placeholder="Enter reason for warning..."
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowWarningModal(null)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: getCardBackground(),
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleIssueWarning(showWarningModal._id, warningReason)
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#f59e0b",
                  color: "#333",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Issue Warning
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* User Action Modal */}
      {selectedUser && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setSelectedUser(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              padding: "24px",
              maxWidth: "450px",
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "16px", color: "#8b5cf6" }}>
              Moderate User
            </h3>
            <p
              style={{
                marginBottom: "16px",
                fontSize: "14px",
                color: getTextColor(),
              }}
            >
              User: <strong>{selectedUser.name}</strong>
            </p>

            <div
              style={{
                marginBottom: "16px",
                padding: "10px",
                background: isDarkMode
                  ? "rgba(139,92,246,0.05)"
                  : "rgba(139,92,246,0.03)",
                borderRadius: "10px",
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <span style={{ color: getTextColor() }}>
                Status:{" "}
                {selectedUser.isMuted ? (
                  <span style={{ color: "#f59e0b" }}>🔇 Muted</span>
                ) : (
                  <span style={{ color: "#10b981" }}>🎤 Active</span>
                )}
              </span>
              {selectedUser.isBanned && (
                <span style={{ color: "#ef4444" }}>🚫 Banned</span>
              )}
              {selectedUser.chatWarningCount > 0 && (
                <span style={{ color: "#8b5cf6" }}>
                  ⚠️ Warnings: {selectedUser.chatWarningCount}/3
                </span>
              )}
            </div>

            {/* Mute Section */}
            <div
              style={{
                marginBottom: "20px",
                borderTop: `1px solid ${getBorderColor()}`,
                paddingTop: "16px",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: getTextColor(),
                }}
              >
                <FaMicrophoneSlash size={14} /> Mute Controls
              </h4>
              {selectedUser.isMuted ? (
                <button
                  onClick={() =>
                    handleUnmuteUser(selectedUser.userId || selectedUser._id)
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <FaMicrophone size={14} /> Unmute User
                </button>
              ) : (
                <>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "block",
                      marginBottom: "4px",
                      color: getTextColor(),
                    }}
                  >
                    Mute Duration (days)
                  </label>
                  <select
                    value={muteDuration}
                    onChange={(e) => setMuteDuration(parseInt(e.target.value))}
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginBottom: "12px",
                      borderRadius: "10px",
                      border: `1px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                  <button
                    onClick={() =>
                      handleMuteUser(selectedUser.userId || selectedUser._id)
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      background: "#06b6d4",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <FaMicrophoneSlash size={14} /> Mute User
                  </button>
                </>
              )}
            </div>

            {/* Ban Section */}
            <div
              style={{
                marginBottom: "20px",
                borderTop: `1px solid ${getBorderColor()}`,
                paddingTop: "16px",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: getTextColor(),
                }}
              >
                <FaBan size={14} /> Ban Controls
              </h4>
              {selectedUser.isBanned ? (
                <button
                  onClick={() =>
                    handleUnbanUser(selectedUser.userId || selectedUser._id)
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <FaUserCheck size={14} /> Unban User
                </button>
              ) : (
                <>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "block",
                      marginBottom: "4px",
                      color: getTextColor(),
                    }}
                  >
                    Ban Duration (days)
                  </label>
                  <select
                    value={banDuration}
                    onChange={(e) => setBanDuration(parseInt(e.target.value))}
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginBottom: "12px",
                      borderRadius: "10px",
                      border: `1px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                    <option value={365}>1 year</option>
                  </select>
                  <button
                    onClick={() =>
                      handleBanUser(selectedUser.userId || selectedUser._id)
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <FaBan size={14} /> Ban User
                  </button>
                </>
              )}
            </div>

            {/* Reason Input */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px",
                  color: getTextColor(),
                }}
              >
                Reason (optional for mute/ban)
              </label>
              <textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows="2"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "10px",
                  border: `1px solid ${getBorderColor()}`,
                  background: getInputBackground(),
                  resize: "vertical",
                  fontSize: "13px",
                  color: getTextColor(),
                }}
                placeholder="Enter reason for action..."
              />
            </div>

            <button
              onClick={() => setSelectedUser(null)}
              style={{
                width: "100%",
                padding: "12px",
                background: getCardBackground(),
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "10px",
                cursor: "pointer",
                color: getTextColor(),
              }}
            >
              Close
            </button>
          </motion.div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowReportModal(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              padding: isMobile ? "20px" : "24px",
              maxWidth: "400px",
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "16px", color: "#ef4444" }}>
              Report Message
            </h3>
            <p
              style={{
                marginBottom: "16px",
                fontSize: "13px",
                color: getTextSecondary(),
              }}
            >
              Why are you reporting this message?
            </p>

            <select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "12px",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "12px",
                background: getInputBackground(),
                color: getTextColor(),
                fontSize: "14px",
              }}
            >
              <option value="">Select a reason</option>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment</option>
              <option value="hate_speech">Hate Speech</option>
              <option value="inappropriate_content">
                Inappropriate Content
              </option>
              <option value="personal_info">Personal Information</option>
              <option value="other">Other</option>
            </select>

            <textarea
              placeholder="Additional details (optional)"
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              rows="3"
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "16px",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "12px",
                resize: "vertical",
                background: getInputBackground(),
                color: getTextColor(),
                fontSize: "13px",
              }}
            />

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                onClick={() => setShowReportModal(null)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: getCardBackground(),
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleReportSubmit(showReportModal)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                }}
              >
                Submit Report
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
            100% { opacity: 1; transform: scale(1); }
          }
          footer {
            display: none !important;
          }
        `}
      </style>
    </div>
  );
};

export default ChatPage;
